import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import './RouteLoadingBar.scss';

// Context for managing loading state
const LoadingContext = createContext({
  isLoading: false,
  startLoading: () => {},
  stopLoading: () => {}
});

/**
 * LoadingProvider - Provides loading state context
 */
export const LoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);

  const startLoading = useCallback(() => setIsLoading(true), []);
  const stopLoading = useCallback(() => setIsLoading(false), []);

  return (
    <LoadingContext.Provider value={{ isLoading, startLoading, stopLoading }}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => useContext(LoadingContext);

/**
 * RouteLoadingBar - Shows a progress bar during route transitions
 */
const RouteLoadingBar = () => {
  const { isLoading } = useLoading();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isLoading) {
      setVisible(true);
      setProgress(0);

      // Animate progress
      const timer1 = setTimeout(() => setProgress(30), 100);
      const timer2 = setTimeout(() => setProgress(60), 300);
      const timer3 = setTimeout(() => setProgress(80), 600);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    } else {
      if (visible) {
        setProgress(100);
        const timer = setTimeout(() => {
          setVisible(false);
          setProgress(0);
        }, 200);
        return () => clearTimeout(timer);
      }
    }
  }, [isLoading, visible]);

  if (!visible) return null;

  return (
    <div className="route-loading-bar">
      <div
        className="route-loading-bar__progress"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

/**
 * LoadingTrigger - Component that triggers loading state when rendered
 * Use this as a Suspense fallback to show the loading bar
 */
export const LoadingTrigger = ({ children }) => {
  const { startLoading, stopLoading } = useLoading();

  useEffect(() => {
    startLoading();
    return () => stopLoading();
  }, [startLoading, stopLoading]);

  return children || null;
};

export default RouteLoadingBar;
